/* eslint-disable @typescript-eslint/member-ordering */
/* tslint:disable: member-ordering*/
import { of as observableOf, Observable } from 'rxjs';

import {
  share,
  exhaustMap,
  concatMap,
  catchError,
  mergeMap,
  map,
  withLatestFrom,
  first,
  tap,
  filter,
  switchMap,
  distinctUntilChanged,
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StocksMarketAction } from './stocks-market.action';
import { ErrorsType } from '../../enum/errors.type';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { BackendService } from '../../services/backend/backend.service';
import { StockTrackingChartInterval } from '../../components/charts/stock-tracking-chart/stock-tracking-chart-interval';
import { RoutesTable } from '../../resources/routes-table.model';
import { RealtimeHub } from '../../enum/realtime/realtime-hub.enum';
import { StocksMarketState } from './stocks-market.reducer';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { RealtimeQuoteService } from '../../services/realtime/realtime-quote.service';
import { UserManagerService } from '../../services/users/user-manager.service';
import { Roles } from '../../models/roles/roles.enum';

@Injectable()
export class StocksMarketEffects {
  constructor(
    private actions$: Actions<StocksMarketAction>,
    private store: Store<AppState>,
    private backendService: BackendService,
    private realtimeService: RealtimeService,
    private quoteApiService: RealtimeQuoteService
  ) {
    this.subscribeQuoteApi();
  }

  /**
   * Subscribe QuoteApi subject
   **/
  private subscribeQuoteApi() {
    this.quoteApiService.quoteApiSubjectObs().subscribe((update) => {
      this.onIncomingData({
        type: StocksMarketAction.getQuoteSuccess,
        payload: update,
      });
    });
  }

  /**
   * Abertura do mercado
   **/
  marketClosing$ = createEffect(
    () => this.actions$.pipe(ofType(StocksMarketAction.marketClosed)),
    { dispatch: false }
  );

  /**
   * Encerramento (fechado) do mercado
   **/
  marketOpening$ = createEffect(
    () => this.actions$.pipe(ofType(StocksMarketAction.marketOpen)),
    { dispatch: false }
  );

  /**
   * Status do mercado (fechado ou aberto)
   *
   **/
  getMarketStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StocksMarketAction.getMarketStatus),
        withLatestFrom(this.store.select((s) => s.stocksExchange.stocksMarket)),
        first(),
        switchMap(() =>
          this.realtimeService.subscribeToMarketStatus().pipe(
            map((market: any) => market.IsOpen),
            distinctUntilChanged(),
            tap((isOpen: any) => {
              const action = isOpen
                ? { type: StocksMarketAction.marketOpen }
                : { type: StocksMarketAction.marketClosed };
              this.onIncomingData(action);
            })
          )
        )
      ),
    { dispatch: false }
  );

  /**
   * Tendencia para ativo/ativos
   * */
  getTrends$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StocksMarketAction.getTrends),
        filter(
          (action) =>
            this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
              RealtimeHub.Tendency,
              action.payload
            ) == true
        ),
        tap((action) => {
          //subscribe to unique symbol tendency
          if (typeof action.payload == 'string') {
            this.realtimeService
              .subscribeToTendency(action.payload)
              .subscribe((tendency) => {
                this.onIncomingData({
                  type: StocksMarketAction.getTrendsSuccess,
                  payload: tendency,
                });
              });
          }
          //subscribe by array
          if (Array.isArray(action.payload)) {
            this.realtimeService
              .subscribeArrayTendency(action.payload)
              .subscribe((tendency) => {
                this.onIncomingData({
                  type: StocksMarketAction.getTrendsSuccess,
                  payload: tendency,
                });
              });
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Cancela inscrições de tendência a partir de um array de ativos
   */
  unsubscribeTrends$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StocksMarketAction.unsubscribeTrends),
        tap((action: any) => {
          this.realtimeService
            .unsubscribeArrayTendency(action.payload)
            .subscribe((result) => {
              if (result) {
                this.store.dispatch({
                  type: StocksMarketAction.unsubscribeTrendsSuccess,
                  payload: action.payload,
                });
              } else {
                this.store.dispatch({
                  type: StocksMarketAction.unsubscribeTrendsError,
                  payload: action.payload,
                });
              }
            });
        })
      ),
    { dispatch: false }
  );

  /**
   * Cotação de ativo/ativos
   */
  getQuote$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StocksMarketAction.getQuote),
        tap((action) => {
          const { role } = UserManagerService.getPreviousUserData();
          if (role == Roles.Activated) {
            if (typeof action.payload == 'string')
              this.quoteApiService.subscribeActiveQuote(action.payload);
            else this.quoteApiService.subscribeActivesArrayQuote(action.payload);
          } else {
            if (typeof action.payload == 'string')
              this.realtimeService
                .subscribeActiveQuote(action.payload)
                .subscribe((update) => {
                  this.onIncomingData({
                    type: StocksMarketAction.getQuoteSuccess,
                    payload: update,
                  });
                });
            else
              this.realtimeService
                .subscribeActivesArrayQuote(action.payload)
                .subscribe((update) => {
                  this.onIncomingData({
                    type: StocksMarketAction.getQuoteSuccess,
                    payload: update,
                  });
                });
          }
        })
      ),
    { dispatch: false }
  );

  /** Para a cotação de ativos de ativos */
  unsubscribeQuote$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StocksMarketAction.unsubscribeQuotes),
        tap((action: any) => {
          this.quoteApiService.unsubscribeActiveArrayQuote(action.payload);
        })
      ),
    { dispatch: false }
  );

  getInstrument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getInstrumentInfo),
      withLatestFrom(this.store.select((s) => s.stocksExchange.stocksMarket)),
      mergeMap(([action, market]) => {
        const normalizedStockId = action.payload.toUpperCase();

        if (market.instruments[normalizedStockId]) {
          return observableOf({
            type: StocksMarketAction.getInstrumentInfoSuccess,
            payload: market.instruments[normalizedStockId],
          });
        }

        // Request pendente
        if (market.pendingInstruments.find((i) => i == normalizedStockId))
          return observableOf({
            type: StocksMarketAction.gettingInstrumentInfo,
            payload: normalizedStockId,
          });

        // Buscando instrumento
        this.store.dispatch({
          type: StocksMarketAction.gettingInstrumentInfo,
          payload: normalizedStockId,
        });
        const url = RoutesTable.composeUri(
          RoutesTable.quote.routes.getInstrumentInfo,
          normalizedStockId
        );
        return this.backendService.get<DefaultResponse>(url).pipe(
          map((result) => {
            if (result.ok)
              return {
                type: StocksMarketAction.getInstrumentInfoSuccess,
                payload: result.value,
              };

            return {
              type: StocksMarketAction.getInstrumentInfoError,
              payload: {
                type: ErrorsType.Error,
                error: result.messages,
                code: normalizedStockId,
              },
            };
          }),
          catchError((error) =>
            observableOf({
              type: StocksMarketAction.getInstrumentInfoError,
              payload: { error: error, code: action.payload.toUpperCase() },
            })
          )
        );
      }),
      share()
    )
  );

  getInstrumentByArray$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getInstrumentsInfoByArray),
      // .withLatestFrom(this.store.select(s => s), (action, state) => {return {action: action, market: state.stocksExchange.stocksMarket}})
      concatMap((action) => {
        let market: StocksMarketState = null;
        this.store
          .select((s) => s.stocksExchange.stocksMarket)
          .subscribe((data) => (market = data));

        const normalizedStockId: Array<string> = action.payload
          .filter((symbol) => symbol)
          .map((symbol) => symbol.toUpperCase());

        const notAlredyHaveInstruments = normalizedStockId.filter(
          (symbol) => !market.instruments[symbol]
        );
        const alredyHaveInstruments = normalizedStockId.filter(
          (symbol) => market.instruments[symbol]
        );

        if (!notAlredyHaveInstruments.length) {
          return observableOf({
            type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
            payload: [],
          });
        } else if (alredyHaveInstruments.length) {
          this.store.dispatch({
            type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
            payload: [],
          });
        }

        const notGetting = notAlredyHaveInstruments.filter(
          (symbol) =>
            market.pendingInstruments.find((item) => item == symbol) == null
        );
        this.store.dispatch({
          type: StocksMarketAction.gettingInstrumentsArrayInfo,
          payload: notGetting,
        });

        const filter = notAlredyHaveInstruments.reduce(
          (prev, current, index, array) =>
            prev +
            ` Code eq '${current}' ${index == array.length - 1 ? '' : 'or'}`,
          ''
        );

        const url = RoutesTable.composeUri(
          RoutesTable.quote.routes.getInstrumentsInfoByArray,
          filter
        );
        return this.backendService.get<DefaultResponse>(url).pipe(
          map((result) => {
            if (result.ok) {
              return {
                type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
                payload: result.value,
              };
            }

            return {
              type: StocksMarketAction.getInstrumentsInfoByArrayError,
              payload: [
                {
                  type: ErrorsType.Error,
                  error: result.messages,
                  code: notGetting,
                },
              ],
            };
          }),
          catchError((error) =>
            observableOf({
              type: StocksMarketAction.getInstrumentsInfoByArrayError,
              payload: [{ error: error, code: action.payload }],
            })
          )
        );
      }),
      share()
    )
  );

  /**
   * Para o snapshot do ativo
   */
  getQuoteInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getQuoteInfo),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteInfo)
      ),
      switchMap(([action, state]) => {
        const normalizedStockId = action.payload.toUpperCase();
        const url = RoutesTable.composeUri(
          RoutesTable.quote.routes.getQuoteInfo,
          normalizedStockId
        );
        return this.backendService.get<DefaultResponse>(url).pipe(
          map((result) => {
            if (result.ok)
              return {
                type: StocksMarketAction.getQuoteInfoSuccess,
                payload: result.value,
              };
            return {
              type: StocksMarketAction.getQuoteInfoError,
              payload: result.value,
            };
          }),
          catchError((error) => {
            return observableOf({
              type: StocksMarketAction.getQuoteInfoError,
              payload: error,
            });
          })
        );
      }),
      share()
    )
  );

  /** Histórico da última hora */
  getLastHourHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getLastHourHistory),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
      ),
      //.filter( ([action, state]) => !state[action.payload] || !!state[action.payload][StockTrackingChartInterval.OneHour])
      exhaustMap(([action, state]) => {
        action.payload = action.payload.toUpperCase();

        if (
          state[action.payload] &&
          state[action.payload][StockTrackingChartInterval.OneHour]
        )
          return observableOf({
            type: StocksMarketAction.getLastHourHistorySuccess,
            payload: {
              code: action.payload,
              interval: StockTrackingChartInterval.OneHour,
              data: state[action.payload][StockTrackingChartInterval.OneHour],
            },
          });

        return this.backendService
          .get<DefaultResponse>(
            RoutesTable.composeUri(
              RoutesTable.quote.routes.lastHourHistory,
              action.payload
            )
          )
          .pipe(
            map((result) => {
              if (result.ok)
                return {
                  type: StocksMarketAction.getLastHourHistorySuccess,
                  payload: {
                    code: action.payload,
                    interval: StockTrackingChartInterval.OneHour,
                    data: result.value,
                  },
                };

              return {
                type: StocksMarketAction.getLastHourHistoryError,
                payload: result.value,
              };
            }),
            catchError((error) => {
              return observableOf({
                type: StocksMarketAction.getLastHourHistoryError,
                payload: error,
              });
            })
          );
      }),
      share()
    )
  );

  /** Histórico do último dia */
  getLastDayHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getLastDayHistory),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
      ),
      //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneDay])
      exhaustMap(([action, state]) => {
        action.payload = action.payload.toUpperCase();

        if (
          state[action.payload] &&
          state[action.payload][StockTrackingChartInterval.OneDay]
        )
          return observableOf({
            type: StocksMarketAction.getLastDayHistorySuccess,
            payload: {
              code: action.payload,
              interval: StockTrackingChartInterval.OneDay,
              data: state[action.payload][StockTrackingChartInterval.OneDay],
            },
          });

        return this.backendService
          .get<DefaultResponse>(
            RoutesTable.composeUri(
              RoutesTable.quote.routes.lastDayHistory,
              action.payload
            )
          )
          .pipe(
            map((result) => {
              if (result.ok)
                return {
                  type: StocksMarketAction.getLastDayHistorySuccess,
                  payload: {
                    code: action.payload,
                    interval: StockTrackingChartInterval.OneDay,
                    data: result.value,
                  },
                };

              return {
                type: StocksMarketAction.getLastDayHistoryError,
                payload: result.value,
              };
            }),
            catchError((error) => {
              return observableOf({
                type: StocksMarketAction.getLastDayHistoryError,
                payload: error,
              });
            })
          );
      }),
      share()
    )
  );

  /** Histórico da última semana */
  getLastWeekHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getLastWeekHistory),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
      ),
      //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneWeek])
      exhaustMap(([action, state]) => {
        action.payload = action.payload.toUpperCase();

        if (
          state[action.payload] &&
          state[action.payload][StockTrackingChartInterval.OneWeek]
        )
          return observableOf({
            type: StocksMarketAction.getLastWeekHistorySuccess,
            payload: {
              code: action.payload,
              interval: StockTrackingChartInterval.OneWeek,
              data: state[action.payload][StockTrackingChartInterval.OneWeek],
            },
          });

        return this.backendService
          .get<DefaultResponse>(
            RoutesTable.composeUri(
              RoutesTable.quote.routes.lastWeekHistory,
              action.payload
            )
          )
          .pipe(
            map((result) => {
              if (result.ok)
                return {
                  type: StocksMarketAction.getLastWeekHistorySuccess,
                  payload: {
                    code: action.payload,
                    interval: StockTrackingChartInterval.OneWeek,
                    data: result.value,
                  },
                };

              return {
                type: StocksMarketAction.getLastWeekHistoryError,
                payload: result.value,
              };
            }),
            catchError((error) => {
              return observableOf({
                type: StocksMarketAction.getLastWeekHistoryError,
                payload: error,
              });
            })
          );
      }),
      share()
    )
  );

  /** Histórico do último mês */
  getLastMonthHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getLastMonthHistory),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
      ),
      //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneMonth])
      exhaustMap(([action, state]) => {
        action.payload = action.payload.toUpperCase();

        if (
          state[action.payload] &&
          state[action.payload][StockTrackingChartInterval.OneMonth]
        )
          return observableOf({
            type: StocksMarketAction.getLastMonthHistorySuccess,
            payload: {
              code: action.payload,
              interval: StockTrackingChartInterval.OneMonth,
              data: state[action.payload][StockTrackingChartInterval.OneMonth],
            },
          });

        return this.backendService
          .get<DefaultResponse>(
            RoutesTable.composeUri(
              RoutesTable.quote.routes.lastMonthHistory,
              action.payload
            )
          )
          .pipe(
            map((result) => {
              if (result.ok)
                return {
                  type: StocksMarketAction.getLastMonthHistorySuccess,
                  payload: {
                    code: action.payload,
                    interval: StockTrackingChartInterval.OneMonth,
                    data: result.value,
                  },
                };

              return {
                type: StocksMarketAction.getLastMonthHistoryError,
                payload: result.value,
              };
            }),
            catchError((error) => {
              return observableOf({
                type: StocksMarketAction.getLastMonthHistoryError,
                payload: error,
              });
            })
          );
      }),
      share()
    )
  );

  /** Histórico do último ano*/
  getLastYearHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getLastYearHistory),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
      ),
      //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneYear])
      exhaustMap(([action, state]) => {
        action.payload = action.payload.toUpperCase();

        if (
          state[action.payload] &&
          state[action.payload][StockTrackingChartInterval.OneYear]
        )
          return observableOf({
            type: StocksMarketAction.getLastYearHistorySuccess,
            payload: {
              code: action.payload,
              interval: StockTrackingChartInterval.OneYear,
              data: state[action.payload][StockTrackingChartInterval.OneYear],
            },
          });

        return this.backendService
          .get<DefaultResponse>(
            RoutesTable.composeUri(
              RoutesTable.quote.routes.lastYearHistory,
              action.payload
            )
          )
          .pipe(
            map((result) => {
              if (result.ok)
                return {
                  type: StocksMarketAction.getLastYearHistorySuccess,
                  payload: {
                    code: action.payload,
                    interval: StockTrackingChartInterval.OneYear,
                    data: result.value,
                  },
                };

              return {
                type: StocksMarketAction.getLastYearHistoryError,
                payload: result.value,
              };
            }),
            catchError((error) => {
              return observableOf({
                type: StocksMarketAction.getLastYearHistoryError,
                payload: error,
              });
            })
          );
      }),
      share()
    )
  );

  /** Histórico dos últimos 5 ano*/
  getLastFiveYearsHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getLastFiveYearsHistory),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
      ),
      exhaustMap(([action, state]) => {
        action.payload = action.payload.toUpperCase();

        if (
          state[action.payload] &&
          state[action.payload][StockTrackingChartInterval.FiveYears]
        )
          return observableOf({
            type: StocksMarketAction.getLastFiveYearsHistorySuccess,
            payload: {
              code: action.payload,
              interval: StockTrackingChartInterval.FiveYears,
              data: state[action.payload][StockTrackingChartInterval.FiveYears],
            },
          });

        return this.backendService
          .get<DefaultResponse>(
            RoutesTable.composeUri(
              RoutesTable.quote.routes.lastFiveYearsHistory,
              action.payload
            )
          )
          .pipe(
            map((result) => {
              if (result.ok) {
                const points = [];
                for (let i = result.value.length - 1; i >= 0; i--) {
                  if (i == 0 || (i + 1) % 5 == 0) {
                    points.unshift(result.value[i]);
                  }
                }
                return {
                  type: StocksMarketAction.getLastFiveYearsHistorySuccess,
                  payload: {
                    code: action.payload,
                    interval: StockTrackingChartInterval.FiveYears,
                    data: points,
                  },
                };
              }

              return {
                type: StocksMarketAction.getLastFiveYearsHistoryError,
                payload: result.value,
              };
            }),
            catchError((error) => {
              return observableOf({
                type: StocksMarketAction.getLastFiveYearsHistoryError,
                payload: error,
              });
            })
          );
      }),
      share()
    )
  );

  /**
   * Histórico de cotação de determinado ativo
   */
  getQuoteHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksMarketAction.getQuoteHistory),
      withLatestFrom(
        this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
      ),
      switchMap(([action, state]) => {
        const url: string = `quotehistory/${
          action.payload.interval
        }/${action.payload.symbol.toUpperCase()}/${action.payload.top}`;
        return this.backendService.get<DefaultResponse>(url).pipe(
          map((result) => {
            if (result.ok)
              return {
                type: StocksMarketAction.getQuoteHistorySuccess,
                payload: {
                  code: action.payload.symbol,
                  interval: action.payload.codeInterval, //Verificar novos intervalos 1m, 15m e 1D
                  data: result.value,
                },
              };

            return {
              type: StocksMarketAction.getQuoteHistoryError,
              payload: result.value,
            };
          }),
          catchError((error) => {
            return observableOf({
              type: StocksMarketAction.getQuoteHistoryError,
              payload: error,
            });
          })
        );
      }),
      share()
    )
  );

  /** Erro ao buscar dados do histórico para o gráfico */
  chartDataRetrievingError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          StocksMarketAction.getLastHourHistoryError,
          StocksMarketAction.getLastDayHistoryError,
          StocksMarketAction.getLastWeekHistoryError,
          StocksMarketAction.getLastMonthHistoryError,
          StocksMarketAction.getLastTwoMonthsHistoryError,
          StocksMarketAction.getLastYearHistoryError,
          StocksMarketAction.getLastFiveYearsHistoryError
        )
      ),
    { dispatch: false }
  );

  private onIncomingData(action: StocksMarketAction) {
    this.store.dispatch(action);
  }
}